import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyModule from '@z-components/molecules/EmptyModule';
import ProBlogPostList from 'components/molecules/ProBlogPostList';
import PhotoList from '@z-components/molecules/PhotoList';
import SelfIntroduction from '@z-components/molecules/SelfIntroduction';
import PricingMenuItemList from 'components/molecules/PricingMenuItemList';

import TabNavigation from 'components/molecules/TabNavigation';
import VerticalTabButtonGroup from 'components/molecules/VerticalTabButtonGroup';

import ClientReviewCardGroup from 'components/organisms/ClientReviewCardGroup';
import ProMatchingCriteria from 'modules/marketing-pages/components/ListPros/ProMatchingCriteria';
import ListCaseStudies from 'modules/users/modules/profiles/components/ListCaseStudies';

import { JobProfile, ProProfilePro, PricingMenuItem, QuestionAnswer } from '../../types';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';

import styles from './style.module.scss';
import JournalGroup from 'components/molecules/JournalGroup';
import { getInternalBlogPostsByJobTypeId } from 'modules/marketing-pages/service';

import {
  trackingEventForRequestCreation,
  GA4EventRequestCreation,
} from 'utils/eventTrackingForGA4';
import { isMobile } from 'utils/layout';

interface ReducedJobProfiles {
  [lv2CategoryName: string]: Array<{ categoryTitle?: string; id: string; label: string }>;
}

interface Props {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  internalBlogCostPosts: Array<BlogPost>;
  jobTypeId?: string;
  setJobTypeTitle: (jobTypeId: string) => void;
  pro: ProProfilePro;
}

const CLIENT_REVIEW_CARD_GROUP_INITIAL_DISPLAY_COUNT = 3;
const CLIENT_REVIEW_CARD_GROUP_INITIAL_DISPLAY_COUNT_MOBILE = 5;
const getReducedJobProfiles = (jobProfiles: Array<JobProfile>) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  jobProfiles.reduce((acc: ReducedJobProfiles, cur: JobProfile) => {
    let lv2CategoryName: string;

    if (!cur.categories) return acc;
    // handle category lists with no c3 value
    if (cur.categories.length > 2) {
      [, lv2CategoryName] = cur.categories;
    } else {
      [lv2CategoryName] = cur.categories;
    }

    // dedupe verticals
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (acc[lv2CategoryName]) {
      const duplicate = acc[lv2CategoryName].find(
        (label: { label: string }) => label.label === cur.label
      );
      if (duplicate !== undefined) {
        return acc;
      }
    }

    const categoryTitle = cur.categoryTranslation.find((t) => t._id === lv2CategoryName)?.title;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (acc[lv2CategoryName]) {
      return {
        ...acc,
        [lv2CategoryName]: [
          ...acc[lv2CategoryName],
          { categoryTitle, id: cur.jobTypeId, label: cur.label },
        ],
      };
    }
    return {
      ...acc,
      [lv2CategoryName]: [{ categoryTitle, id: cur.jobTypeId, label: cur.label }],
    };
  }, {});

const RAND_KEY_RADIX = 36;
const RAND_KEY_LENGTH = 7;
const randomKey = () => Math.random().toString(RAND_KEY_RADIX).substring(RAND_KEY_LENGTH);

// TODO: move translation to json

const ProServiceContent: FC<Props> = ({
  featureFlags,
  internalBlogPosts,
  internalBlogCostPosts,
  jobTypeId: currentJobTypeId,
  pro,
  setJobTypeTitle,
}) => {
  const {
    blogPosts = [],
    jobProfiles = [],
    pricingMenus,
    proProfile,
    reviews = {
      counts: [0, 0, 0, 0, 0],
      list: [],
      total: 0,
    },
    slug,
  } = pro;

  const {
    i18n: { language },
    t,
  } = useTranslation();
  const reducedJobProfiles = getReducedJobProfiles(jobProfiles);
  // Get list of Lv2 category names.
  const lv2CategoryNames = Object.keys(reducedJobProfiles).map((category2) => category2);

  const ITEM_LENGTH_TO_COLLAPSE = 5;

  const [selectedC2TabName, setSelectedC2TabName] = useState('');
  const [areVerticalsCollapsed, setAreVerticalsCollapsed] = useState(true);
  const [selectedVertical, setSelectedVertical] = useState(currentJobTypeId ?? '');

  // Category Lv2 Tabs
  const handleC2TabClick = useCallback(
    (c2TabName: string) => {
      setSelectedC2TabName(c2TabName);
      // init selection of vertical
      const firstJobTypeTabName = reducedJobProfiles[c2TabName][0].id;
      setSelectedVertical(firstJobTypeTabName);

      // if currentJobTypeId is null select 1st item of VT list
      if (!currentJobTypeId) {
        const c2Verticals = reducedJobProfiles[c2TabName];
        setSelectedVertical(c2Verticals[0].id);
      }
      setAreVerticalsCollapsed(true);
    },
    [currentJobTypeId, reducedJobProfiles]
  );

  // Vertical (JobType) Tabs
  useEffect(() => {
    if (selectedVertical) {
      const jobType = jobProfiles.find((item) => item.jobTypeId === selectedVertical);
      const categories = jobType?.categories;
      setSelectedC2TabName(categories?.[1] ?? '');

      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed, {
        jobTypeId: selectedVertical,
        // eslint-disable-next-line id-length
        C1: categories?.[0],
        // eslint-disable-next-line id-length
        C2: categories?.[1],
      });
    } else {
      setSelectedC2TabName(lv2CategoryNames[0]);
      const c2Verticals = reducedJobProfiles[lv2CategoryNames[0]];
      setSelectedVertical(c2Verticals[0].id);
      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobProfiles]);

  // content: pricing menu
  const [visiblePricingMenu, setVisiblePricingMenu] = useState<Array<PricingMenuItem>>([]);
  useEffect(() => {
    const filterPricingMenu = (jobTypeId: string) => {
      if (pricingMenus) {
        const pricingMenu = pricingMenus.find((item) => item.jobTypeId === jobTypeId);
        setVisiblePricingMenu(pricingMenu?.pricingMenus ?? []);
      } else {
        setVisiblePricingMenu([]);
      }
    };

    if (currentJobTypeId) {
      filterPricingMenu(currentJobTypeId);
    }
  }, [pricingMenus, currentJobTypeId]);

  // content: "service" tab
  const getVerticalTabsContent = () => {
    const handleVerticalSelection = async (jobTypeId: string) => {
      const filterPricingMenu = (jobTypeIdToFilter: string) => {
        if (pricingMenus) {
          const pricingMenu = pricingMenus.find((item) => item.jobTypeId === jobTypeIdToFilter);
          setVisiblePricingMenu(pricingMenu?.pricingMenus ?? []);
        } else {
          setVisiblePricingMenu([]);
        }
      };
      setJobTypeTitle(jobTypeId);
      filterPricingMenu(jobTypeId);

      setCurrentBlogPosts(jobTypeId ? await getInternalBlogPostsByJobTypeId({ jobTypeId }) : []);
      setCurrentBlogCostPosts(
        jobTypeId
          ? await getInternalBlogPostsByJobTypeId({
              jobTypeId,
              isCostTag: true,
            })
          : []
      );
    };

    // Build C2 tab name list: using Lv2 category name as tab name
    const c2TabsOrder: { [tabName: string]: number } = {};
    lv2CategoryNames.forEach((item, index) => {
      c2TabsOrder[item] = index;
    });

    const tabsContent = [
      ...Object.keys(reducedJobProfiles).map((category2) => ({
        content: (
          <VerticalTabButtonGroup
            areVerticalsCollapsed={areVerticalsCollapsed}
            background={true}
            items={reducedJobProfiles[category2]}
            itemLengthToCollapse={ITEM_LENGTH_TO_COLLAPSE}
            language={language}
            slug={slug}
            // eslint-disable-next-line react/jsx-no-bind
            onItemClick={handleVerticalSelection}
            selectedC2TabName={reducedJobProfiles[category2][0].categoryTitle ?? ''}
            selectedItem={selectedVertical}
            // eslint-disable-next-line react/jsx-no-bind
            selectVertical={setSelectedVertical}
            setAreVerticalsCollapsed={setAreVerticalsCollapsed}
          />
        ),
        label: reducedJobProfiles[category2][0].categoryTitle,
        key: randomKey(),
        showArrow: false,
      })),
    ];
    return { tabsContent, c2TabsOrder };
  };

  const jobTypeTitle = jobProfiles.find((type) => type.jobTypeId === selectedVertical)?.label;
  const jobTypeLabel = jobTypeTitle ?? '';

  // content: job profiles
  const getSelectedProfile = (jobTypeId: string) =>
    jobProfiles.find((jobProfile) => jobProfile.jobTypeId === jobTypeId);
  const questionAnswers: Array<QuestionAnswer> =
    getSelectedProfile(selectedVertical)?.questionAnswers ?? [];
  const filteredQuestionAnswers: Array<QuestionAnswer> = questionAnswers.filter(
    (questionAnswer) => questionAnswer.answer
  );

  const [currentBlogPosts, setCurrentBlogPosts] = useState<Array<BlogPost>>(internalBlogPosts);
  const [currentBlogCostPosts, setCurrentBlogCostPosts] =
    useState<Array<BlogPost>>(internalBlogCostPosts);

  const { tabsContent, c2TabsOrder } = getVerticalTabsContent();

  const proMatchingCriteria = (
    <ProMatchingCriteria
      language={language as 'en' | 'ja'}
      matchingCriteria={getSelectedProfile(selectedVertical)?.matchingCriteria ?? []}
    />
  );

  return (
    <div className={styles['pro-service-c2-tab']}>
      <TabNavigation
        style="hollow"
        activeTabName={selectedC2TabName}
        list={tabsContent}
        onTabClick={handleC2TabClick}
        tabsOrder={c2TabsOrder}
      />

      <div id="content" className={styles.content}>
        {selectedVertical && (
          <section id="job-profile" className={styles['matching-criteria']}>
            {jobTypeLabel && <h3>{t('users:profile.selfIntroduction', { jobTypeLabel })}</h3>}
            <SelfIntroduction
              content={getSelectedProfile(selectedVertical)?.description}
              filteredQuestionAnswers={filteredQuestionAnswers}
              language={language as 'en' | 'ja'}
              title={getSelectedProfile(selectedVertical)?.title}
              proMatchingCriteria={proMatchingCriteria}
            />
          </section>
        )}
        {selectedVertical && (
          <section id="pricing-menus">
            {jobTypeLabel && <h3>{`${jobTypeLabel}${t('users:profile.pricingMenu')}`}</h3>}
            {visiblePricingMenu.length > 0 ? (
              <PricingMenuItemList
                items={visiblePricingMenu.map(
                  ({ description, key, price, priceRange, isMinimum }) => ({
                    description,
                    key,
                    price,
                    priceRangeStart: priceRange?.min,
                    priceRangeEnd: priceRange?.max,
                    isMinimum,
                  })
                )}
                variant="flat"
              />
            ) : (
              <EmptyModule language={language as 'en' | 'ja'} text={t('users:profile.menu')} />
            )}
          </section>
        )}
        {selectedVertical && jobTypeTitle && (
          <section id="case-study-in-service-profile" className={styles['case-study']}>
            <ListCaseStudies
              title={{
                base: t('users:profile.caseStudyBase'),
                prefix: t('users:profile.caseStudyPrefix', { jobTypeLabel }),
              }}
              variant={isMobile(window.screen.width) ? 'swipe' : ''}
            />
          </section>
        )}
        {selectedVertical && (
          <section id="reviews">
            <ClientReviewCardGroup
              initialDisplayCount={
                isMobile(window.screen.width)
                  ? CLIENT_REVIEW_CARD_GROUP_INITIAL_DISPLAY_COUNT_MOBILE
                  : CLIENT_REVIEW_CARD_GROUP_INITIAL_DISPLAY_COUNT
              }
              isReviewsQAEnabled={featureFlags.isReviewsQAEnabled}
              reviews={reviews.list
                .filter((review) => review.jobTypeId === selectedVertical)
                .map((review) => ({
                  shortName: review.shortName ?? '',
                  ...review,
                }))}
              title={{
                en: t('users:profile.reviewJobType', { jobTypeLabel }),
                ja: t('users:profile.reviewJobType', { jobTypeLabel }),
              }}
              variant={isMobile(window.screen.width) ? 'swipe' : ''}
              centerSeeMore={true}
            />
          </section>
        )}
        {proProfile.photos && proProfile.photos.length > 0 && selectedVertical && (
          <>
            <section id="photos-desktop" className={styles['photos-desktop']}>
              <PhotoList
                name={''}
                translations={{
                  jobType: jobTypeLabel,
                  noCaption: t('users:profile.noCaption'),
                  photos: t('users:profile.photos'),
                  seeMoreLabel: t('common:seeMore'),
                }}
                photos={proProfile.photos}
                rowLimit={10}
              />
            </section>
            <section id="photos-mobile" className={styles['photos-mobile']}>
              <PhotoList
                name={''}
                translations={{
                  jobType: jobTypeLabel,
                  noCaption: t('users:profile.noCaption'),
                  photos: t('users:profile.photos'),
                }}
                photos={proProfile.photos}
                photoMargin={16}
              />
            </section>
          </>
        )}
        {blogPosts.length > 0 && (
          <section id="blog-posts">
            <h3 className={styles['title']}>
              {t('marketingPages:proBlogPostSection.proProfileTitle')}
            </h3>
            <ProBlogPostList posts={blogPosts} />
          </section>
        )}
        {currentBlogPosts.length > 0 && (
          <section id="internal-blog-posts">
            <h3>
              {t('marketingPages:latestArticleSectionTitle', {
                jobType: jobTypeTitle,
              })}
            </h3>
            <JournalGroup posts={currentBlogPosts} />
          </section>
        )}
        {currentBlogCostPosts.length > 0 && (
          <section id="internal-blog-cost-posts">
            <h3>
              {t('marketingPages:latestCostArticleSectionTitle', {
                jobType: jobTypeTitle,
              })}
            </h3>
            <JournalGroup posts={currentBlogCostPosts} />
          </section>
        )}
      </div>
    </div>
  );
};

export type { Props as ProServiceContentProps };
export default ProServiceContent;
